import React from "react";
import { styled } from "styled-components";

import { IAuctionPricePreviewProps } from "./lib/DataTable.type";
import { AnimatedWrapper } from "./lib/animationStyle";
import { useLocation } from "react-router-dom";

type AuctionPriceKey = keyof typeof koreanLabels;

const koreanLabels: Record<string, string> = {
    registrationTax: "등록세",
    educationTax: "교육세",
    filingFees: "등기신청수수료",
    deliveryCharges: "송달료",
    biddingFees: "유찰수수료",
    newsAdvertisingRates: "신문공고료",
    appraisalFees: "감정료",
    statusSurveyFees: "현황조사료",
    divestitureFees: "매각수수료",
    auctionPrice: "총 경매비용",
};

const descriptions: Record<string, string> = {
    registrationTax: "청구금액 x 0.002",
    educationTax: "등록세액 x 0.2",
    filingFees: "부동산 1필지 당 3,000원",
    deliveryCharges: "(신청서상 이해관계인수 + 3) x 10회분",
    biddingFees: "유찰 1회 당 6,000원",
    newsAdvertisingRates: "부동산 1필지 당 220,000원\n※경매목적물이 다수인 경우 신문공고료가 추가 발생될 수 있습니다.",
    appraisalFees: "※감정가의 60% 기준금액을 바탕으로 감정수수료를 계산되어 실제 금액과 상이할 수 있습니다.",
    statusSurveyFees: "※도서지역 등 특수한 경우에는 현황조사 수수료가 추가 발생될 수 있습니다.",
    divestitureFees: "※매각가의 기준금액에 따라 다르게 부과됩니다.",
    auctionPrice: "※경매비용은 실제 금액과 약간 상이할 수 있습니다.",
};

const orderKeys: AuctionPriceKey[] = Object.keys(koreanLabels) as AuctionPriceKey[];

const AuctionPricePreview = ({ auctionData }: IAuctionPricePreviewProps) => {
    const location = useLocation();
    const type = location.pathname === "/detail/auctionDetail/" ? "wide" : "default";

    const { auctionPrice, auctionPriceNoDataMessageType } = auctionData;

    const renderTableRows = () => {
        if (!auctionPrice || auctionPriceNoDataMessageType === 4 || auctionPriceNoDataMessageType === 5)
            return (
                <tr className={type === "wide" ? "no-data" : "no-data-narrow"}>
                    <td colSpan={type === "wide" ? 3 : 2}>
                        예상 배당표 관련 정보는 매각물건명세서가 제공된 이후 시점 매각기일 7일전부터 제공됩니다
                    </td>
                </tr>
            );

        return orderKeys.map((key) => {
            const isLastRow = key === orderKeys[orderKeys.length - 1];
            const rowClassName = isLastRow ? "last-row" : "";

            if (type === "wide") {
                return (
                    <tr key={key} className={rowClassName}>
                        <th>{koreanLabels[key]}</th>
                        <td>{auctionPrice[key as keyof typeof auctionPrice]}</td>
                        <td>{descriptions[key]}</td>
                    </tr>
                );
            }

            return (
                <React.Fragment key={key}>
                    <tr className={rowClassName}>
                        <th rowSpan={2}>{koreanLabels[key]}</th>
                        <td style={{ textAlign: "left" }}>{auctionPrice[key as keyof typeof auctionPrice]}</td>
                    </tr>
                    <tr className={rowClassName}>
                        <td>{descriptions[key]}</td>
                    </tr>
                </React.Fragment>
            );
        });
    };

    return (
        <AnimatedWrapper>
            <Container>
                <h4>경매비용</h4>
                <div>
                    <table>
                        <colgroup>
                            {type === "wide" ? (
                                <>
                                    <col width="20%" />
                                    <col width="20%" />
                                    <col width="60%" />
                                </>
                            ) : (
                                <>
                                    <col width="30%" />
                                    <col width="70%" />
                                </>
                            )}
                        </colgroup>
                        <tbody>{renderTableRows()}</tbody>
                    </table>
                </div>
            </Container>
        </AnimatedWrapper>
    );
};

const Container = styled.div`
    h4 {
        color: #0c002c;
        font-size: 18px;
        font-weight: 600;
        line-height: 28px;
    }

    table {
        margin-top: 14px;
        width: 100%;
        border-collapse: collapse;

        th {
            color: #ffffff;
            font-size: 14px;
            font-weight: 600;
            line-height: 24px;
            background-color: #17366d;
            vertical-align: middle !important;
        }

        td {
            font-size: 14px;
            font-style: normal;
            font-weight: 400;
            line-height: 24px;
            white-space: pre-wrap;
        }

        /* tr td:nth-child(2) {
            text-align: right;
        } */

        .last-row,
        .last-row th,
        .last-row td {
            background-color: #edf0f6 !important;
            color: #0c002c;
            font-weight: 600;
        }

        th,
        td {
            border: 1px solid #8d8d8d;
            padding: 2px 4px;
        }

        .no-data td {
            border: none !important;
            border-radius: 4px;
            background-color: #f0f0f0;
            padding: 14px;
            color: #8d8d8d;
            font-size: 14px;
            font-weight: 400;
            line-height: 24px;
            text-align: center;
        }

        .no-data-narrow td {
            border: none !important;
            border-radius: 14px;
            background-color: #f0f0f0;
            padding: 14px;
            color: #8d8d8d;
            font-size: 14px;
            font-weight: 600;
            line-height: 24px;
            text-align: center;
        }
    }
`;

export default AuctionPricePreview;
